import React, { useEffect, useState } from "react";
import { openNotification } from "../Notification/Index";
import { ParsedResumeContent } from "../../Interface/Index";
import ResumeReviewForm from "./ResumeReviewForm";
import UserService from "../../Services/user.service";

interface ResumePreviewProps {
  handleFileUploadAndAnalyze?: (
    file: File,
    parsedResumeData: ParsedResumeContent,
  ) => void | Promise<void>;
  resumeFile: File | null;
  handleClose: () => void;
  onReupload?: () => void;
  onStartAnalysis?: (
    resumeData: ParsedResumeContent,
    plainText: string,
  ) => void;
  onSave?: (
    file: File,
    resumeData: ParsedResumeContent,
    plainText: string,
  ) => Promise<void>;
  isDirectUpload?: boolean;
  initialParsedResume?: ParsedResumeContent | null;
}

// Convert FormattedResume to plain text for the API
const formatResumeToPlainText = (resume: ParsedResumeContent): string => {
  const parts: string[] = [];

  // Basic Details
  if (resume.basicDetails) {
    const { firstName, lastName, email, phoneNumber, address } =
      resume.basicDetails;
    const name = [firstName, lastName].filter(Boolean).join(" ");
    if (name) parts.push(`Name: ${name}`);
    if (email) parts.push(`Email: ${email}`);
    if (phoneNumber) parts.push(`Phone: ${phoneNumber}`);
    if (address) parts.push(`Address: ${address}`);
  }

  // Social Links
  if (resume.socialLinks) {
    const { linkedin, github, personalPortfolio } = resume.socialLinks;
    if (linkedin) parts.push(`LinkedIn: ${linkedin}`);
    if (github) parts.push(`GitHub: ${github}`);
    if (personalPortfolio) parts.push(`Portfolio: ${personalPortfolio}`);
  }

  // Summary
  if (resume.summary) {
    parts.push(`\nSummary:\n${resume.summary}`);
  }

  // Experience
  if (resume.experience && resume.experience.length > 0) {
    parts.push("\nWork Experience:");
    resume.experience.forEach((exp) => {
      const title = [exp.designation, exp.companyName]
        .filter(Boolean)
        .join(" at ");
      const period = exp.to
        ? [exp.from, exp.to].filter(Boolean).join(" - ")
        : exp.from
          ? `${exp.from} - Present`
          : "";
      if (title) parts.push(`- ${title}${period ? ` (${period})` : ""}`);
      if (exp.location) parts.push(`  Location: ${exp.location}`);
      if (exp.description) parts.push(`  ${exp.description}`);
    });
  }

  // Education
  if (resume.education && resume.education.length > 0) {
    parts.push("\nEducation:");
    resume.education.forEach((edu) => {
      const title = [edu.degree, edu.school].filter(Boolean).join(" - ");
      const period = edu.to
        ? [edu.from, edu.to].filter(Boolean).join(" - ")
        : edu.from
          ? `${edu.from} - Present`
          : "";
      if (title) parts.push(`- ${title}${period ? ` (${period})` : ""}`);
      if (edu.location) parts.push(`  Location: ${edu.location}`);
      if (edu.description) parts.push(`  ${edu.description}`);
    });
  }

  // Skills
  if (resume.skills && resume.skills.length > 0) {
    parts.push(`\nSkills: ${resume.skills.join(", ")}`);
  }

  // Languages
  if (resume.languages && resume.languages.length > 0) {
    parts.push(`\nLanguages: ${resume.languages.join(", ")}`);
  }

  // Certifications
  if (resume.certifications && resume.certifications.length > 0) {
    parts.push("\nCertifications:");
    resume.certifications.forEach((cert) => {
      const title = [cert.name, cert.issuer].filter(Boolean).join(" - ");
      if (title) parts.push(`- ${title}${cert.year ? ` (${cert.year})` : ""}`);
    });
  }

  // Projects
  if (resume.projects && resume.projects.length > 0) {
    parts.push("\nProjects:");
    resume.projects.forEach((project) => {
      if (project.title) parts.push(`- ${project.title}`);
      if (project.description) parts.push(`  ${project.description}`);
      if (project.technologies && project.technologies.length > 0) {
        parts.push(`  Technologies: ${project.technologies.join(", ")}`);
      }
      if (project.githubLink) parts.push(`  GitHub: ${project.githubLink}`);
      if (project.liveLink) parts.push(`  Live: ${project.liveLink}`);
    });
  }

  // Achievements
  if (resume.achievements && resume.achievements.length > 0) {
    parts.push("\nAchievements:");
    resume.achievements.forEach((achievement) => {
      parts.push(`- ${achievement}`);
    });
  }

  // Other Details
  if (resume.otherDetails) {
    parts.push(`\nAdditional Information:\n${resume.otherDetails}`);
  }

  return parts.join("\n");
};

const EMPTY_RESUME: ParsedResumeContent = {
  basicDetails: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    whatsAppConsent: { consent: true, timestamp: new Date(), history: [] },
  },
  skills: [],
  summary: "",
  experience: [],
  education: [],
  projects: [],
  certifications: [],
  achievements: [],
  languages: [],
  otherDetails: "",
  socialLinks: {
    linkedin: "",
    github: "",
    personalPortfolio: "",
  },
};

const ResumePreview: React.FC<ResumePreviewProps> = ({
  resumeFile,
  handleFileUploadAndAnalyze,
  handleClose,
  onReupload,
  onStartAnalysis,
  onSave,
  isDirectUpload = false,
  initialParsedResume = null,
}) => {
  const [formattedResume, setFormattedResume] =
    useState<ParsedResumeContent | null>(null);
  const [editedResume, setEditedResume] = useState<ParsedResumeContent | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      if (initialParsedResume) {
        setFormattedResume(initialParsedResume);
        setEditedResume(initialParsedResume);
      } else {
        setFormattedResume(EMPTY_RESUME);
        setEditedResume(EMPTY_RESUME);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialParsedResume]);

  const handleFormChange = (data: ParsedResumeContent) => {
    setEditedResume(data);
  };

  const handleConfirm = async () => {
    try {
      if (!editedResume) {
        openNotification(
          "error",
          "No Data",
          "Please fill in your information before continuing.",
        );
        return;
      }

      // Email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Phone number validation - remove formatting and check for digits
      const isPhoneValid = (phone: string | undefined): boolean => {
        if (!phone || phone.trim().length === 0) return false;
        const cleanedPhone = phone.replace(/[\s\-()\\+]/g, "").trim();
        return /^\d{7,15}$/.test(cleanedPhone);
      };

      // Validate required fields: email and phone number
      if (
        !editedResume.basicDetails?.email ||
        editedResume.basicDetails.email.trim().length === 0
      ) {
        openNotification(
          "error",
          "Email Required",
          "Please provide your email address to continue.",
        );
        return;
      }

      if (!emailRegex.test(editedResume.basicDetails.email.trim())) {
        openNotification(
          "error",
          "Invalid Email",
          "Please enter a valid email address.",
        );
        return;
      }

      if (
        !editedResume.basicDetails?.phoneNumber ||
        editedResume.basicDetails.phoneNumber.trim().length === 0
      ) {
        openNotification(
          "error",
          "Phone Number Required",
          "Please provide your phone number to continue.",
        );
        return;
      }

      if (!isPhoneValid(editedResume.basicDetails.phoneNumber)) {
        openNotification(
          "error",
          "Invalid Phone Number",
          "Please enter a valid phone number.",
        );
        return;
      }

      if (!resumeFile) {
        openNotification(
          "error",
          "No Resume Uploaded",
          "Please upload a resume file to begin the analysis.",
        );
        return;
      }

      setIsSubmitting(true);
      const plainText = formatResumeToPlainText(editedResume);
      // Direct upload mode - save the resume
      if (isDirectUpload && onSave) {
        try {
          await onSave(resumeFile, editedResume, plainText);
          handleClose();
        } catch (error) {
          console.error("Error saving resume:", error);
          openNotification(
            "error",
            "Save Failed",
            "Failed to save resume. Please try again.",
          );
        } finally {
          setIsSubmitting(false);
        }
        return;
      }
      if (onStartAnalysis) {
        onStartAnalysis(editedResume, plainText);
      } else if (handleFileUploadAndAnalyze) {
        handleClose();
        await handleFileUploadAndAnalyze(resumeFile, editedResume);
      }
    } catch (error: any) {
      console.error("Error while starting analysis", error);
      openNotification(
        "error",
        "Unable to Start Analysis",
        "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReupload = () => {
    if (onReupload) {
      onReupload();
    }
  };

  return (
    <div className="resume-preview-container">
      <ResumeReviewForm
        formattedResume={formattedResume}
        onFormChange={handleFormChange}
        onReupload={handleReupload}
        onConfirm={handleConfirm}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default ResumePreview;
